import { prixApplique } from './paliers';
import { uid } from './utils';

/**
 * Les blocs : plusieurs articles vendus ensemble à un prix global.
 *
 * <p>Le patron veut parfois facturer « Salle de bain complète » à un seul
 * prix, tout en montrant au client ce qui est compris. Les articles cochés
 * deviennent une seule ligne au forfait, au prix global choisi ; leur détail
 * (les désignations, sans les quantités : le client paie le tout, pas des
 * mètres) s'écrit dans la description de la ligne. Une
 * ligne ordinaire : le document, le PDF du serveur, la TVA et les totaux la
 * traitent comme les autres.
 */

/** Le montant HT d'une ligne, remise de la ligne déduite. */
export function montantLigne(l, remise = +l.rem || 0) {
  return (+l.q || 0) * prixApplique(l) * (1 - remise / 100);
}

/** Unité d'un bloc : le document n'y montre ni quantité ni prix unitaire. */
export const UNITE_BLOC = 'bloc';

/**
 * Vrai pour un bloc à prix global : le document n'en montre que le total,
 * sans « 1 forfait » ni prix unitaire. Son unité le garde après
 * l'enregistrement ; la liste des articles d'origine, elle, ne vit que
 * le temps de la saisie.
 *
 * @param {object} l ligne
 * @returns {boolean} vrai pour un bloc
 */
export function estBloc(l) {
  return Array.isArray(l?.bloc) || l?.u === UNITE_BLOC;
}

/** Le texte en minuscules, sans accents, pour comparer des mots. */
const simple = (t) =>
  String(t || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * Le texte d'un article dans le bloc : sa désignation seule ne suffit pas
 * quand elle est courte (« Déplacement »), le client doit lire ce qui est
 * fait. La description complète la désignation (« Déplacement — mise en
 * place du chantier… ») ; si elle la redit déjà (« Pose du carrelage… »
 * sous « Carrelage »), elle la remplace.
 */
function texteDansLeBloc(l) {
  const d = String(l.d || '').trim();
  const sd = String(l.sd || '')
    .split('\n')
    .map((x) => x.replace(/^[•\-–\s]+/, '').trim())
    .filter(Boolean)
    .join(', ');
  if (!sd) return d;
  const mots = simple(d)
    .split(/[^a-z0-9]+/)
    .filter((m) => m.length >= 4);
  if (mots.length && mots.every((m) => simple(sd).includes(m))) return sd;
  return d + ' — ' + (/^\p{Lu}\p{Ll}/u.test(sd) ? sd.charAt(0).toLowerCase() + sd.slice(1) : sd);
}

/**
 * Le détail d'un bloc : une ligne « • … » par article, avec ce qui est fait.
 * Les mètres n'y figurent pas : le bloc se vend à un prix global, pas au m².
 */
export function detailDuBloc(lignes) {
  return lignes.map((l) => '• ' + texteDansLeBloc(l)).join('\n');
}

/**
 * Faux si les lignes n'ont pas toutes le même taux de TVA : un bloc n'en porte
 * qu'un. Une ligne sans taux propre prend celui du document : un article du
 * catalogue à 8.1 % et une ligne libre au taux du document (8.1 %) vont
 * ensemble — ils étaient refusés, et le bloc ne se faisait pas.
 *
 * @param {object[]} lignes les lignes cochées
 * @param {number} [tauxDocument] taux de TVA du document
 * @returns {boolean} vrai si un seul taux
 */
export function memeTva(lignes, tauxDocument) {
  const taux = new Set(
    lignes.map((l) => {
      if (l.taxe === false) return 'exonere';
      const t = l.tva === '' || l.tva == null ? tauxDocument : l.tva;
      return t == null || t === '' ? '' : String(+t);
    }),
  );
  return taux.size <= 1;
}

/**
 * Le prix tapé par le patron, comme il l'écrit : « 5900 », « 5'900.- »,
 * « CHF 5 900,50 ». Un prix illisible était ignoré sans un mot.
 *
 * @param {string} texte le prix saisi
 * @returns {number|null} le prix, ou null s'il ne se lit pas
 */
export function prixSaisi(texte) {
  const net = String(texte ?? '')
    .replace(/chf|fr\.?/gi, '')
    .replace(/['’\s]/g, '')
    .replace(/[.,]?[-–]+$/, '')
    .replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(net)) return null;
  return Number(net);
}

/**
 * Regroupe des lignes en un bloc, à la place de la première.
 *
 * @param {object[]} lignes les lignes de la pièce
 * @param {Array} ids les lignes à regrouper
 * @param {{nom: string, prix: number}} bloc nom et prix global HT
 * @returns {object[]} les lignes, avec le bloc
 */
export function regroupeEnBloc(lignes, ids, { nom, prix }) {
  const choisies = lignes.filter((l) => ids.includes(l.id));
  if (!choisies.length) return lignes;
  const modele = choisies[0];
  const bloc = {
    id: uid(),
    d: String(nom || '').trim(),
    sd: detailDuBloc(choisies),
    u: UNITE_BLOC,
    q: 1,
    p: Math.round((+prix || 0) * 100) / 100,
    rem: 0,
    taxe: modele.taxe !== false,
    tva: modele.tva,
    // Les articles d'origine, pour défaire le bloc tant que la pièce est ouverte.
    bloc: choisies,
  };
  const resultat = [];
  for (const l of lignes) {
    if (l.id === modele.id) resultat.push(bloc);
    else if (!ids.includes(l.id)) resultat.push(l);
  }
  return resultat;
}

/** Remet les articles d'un bloc à sa place. */
export function defaitLeBloc(lignes, id) {
  return lignes.flatMap((l) => (l.id === id && Array.isArray(l.bloc) ? l.bloc : [l]));
}
